import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Palette, RefreshCw, Sparkles, Share2, Search, Target, FileText, Mail, ArrowRight, Check, ExternalLink, Zap,
  Heart, Users, Award, TrendingUp, Code2, Lightbulb
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Palette,
  RefreshCw,
  Sparkles,
  Share2,
  Search,
  Target,
  FileText,
  Mail,
  Zap,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const staticServices = [
  { id: 1, title: 'Custom Web Design', icon: 'Palette', description: 'We design stunning, conversion-focused websites tailored to your brand. Every site is built mobile-first, fast, and with your customers in mind.', features: ['Custom UI/UX Design', 'Mobile-First Responsive', 'SEO-Ready Structure', 'Fast Load Times', 'CMS Integration', 'Conversion Optimized'] },
  { id: 2, title: 'Website Redesign', icon: 'RefreshCw', description: 'Transform your outdated website into a modern, high-performing digital asset that reflects your brand and drives results.', features: ['Full Visual Overhaul', 'Performance Improvements', 'Modern Tech Stack', 'Brand Alignment', 'UX Audit & Fixes', 'Content Migration'] },
  { id: 3, title: 'Branding & Logo Design', icon: 'Sparkles', description: 'Build a memorable brand identity from the ground up. We create logos, color systems, and brand guidelines that make you unforgettable.', features: ['Logo Design', 'Brand Guidelines', 'Color Palette', 'Typography System', 'Brand Assets', 'Business Card Design'] },
  { id: 4, title: 'Social Media Management', icon: 'Share2', description: 'Grow your audience and stay top-of-mind with consistent, engaging social media content crafted to build community and drive traffic.', features: ['Content Calendar', 'Post Design & Copywriting', 'Community Management', 'Analytics Reporting', 'Hashtag Strategy', 'Platform Management'] },
  { id: 5, title: 'SEO Optimization', icon: 'Search', description: 'Rank higher on Google and attract more organic traffic. Our SEO strategies are data-driven and built for long-term growth.', features: ['Keyword Research', 'On-Page Optimization', 'Technical SEO Audit', 'Link Building', 'Local SEO', 'Monthly Reports'] },
  { id: 6, title: 'AI Receptionist & Automation', icon: 'Zap', description: 'Never miss a lead again. Our AI receptionist handles inquiries, qualifies leads, and books appointments — 24/7.', features: ['24/7 AI Chat', 'Lead Qualification', 'Appointment Booking', 'CRM Integration', 'Custom Workflows', 'Multi-Channel Support'] },
  { id: 7, title: 'Content Creation', icon: 'FileText', description: 'Compelling content that tells your story, educates your audience, and drives action across every digital channel.', features: ['Blog Writing', 'Video Scripts', 'Website Copy', 'Email Sequences', 'Social Captions', 'Brand Storytelling'] },
  { id: 8, title: 'Email Marketing', icon: 'Mail', description: 'Turn your email list into a revenue machine. We design, write, and automate campaigns that keep your audience engaged and buying.', features: ['Campaign Design', 'Copywriting', 'Automation Sequences', 'List Segmentation', 'A/B Testing', 'Performance Analytics'] },
];

// Portfolio items integrated into services
const portfolioByService = {
  'Custom Web Design': [
    { title: 'Caveman Financial', image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', result: 'Finance Research Platform', link: 'https://caveman-financial.com/' },
    { title: 'Plan Testosterone', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', result: 'Health Awareness Hub', link: 'https://plan-testosterone.com/' },
  ],
  'Website Redesign': [
    { title: 'Caveman Financial', image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', result: 'Live Stock Tracking', link: 'https://caveman-financial.com/' },
  ],
  'Branding & Logo Design': [
    { title: 'Plan Testosterone', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', result: 'Brand Identity', link: 'https://plan-testosterone.com/' },
    { title: 'UniGem', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', result: 'App Branding', link: 'https://apps.apple.com/us/app/unigem/id6740437581' },
  ],
  'Social Media Management': [],
  'SEO Optimization': [
    { title: 'Caveman Financial', image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', result: 'SEO Optimized', link: 'https://caveman-financial.com/' },
  ],
  'AI Receptionist & Automation': [],
  'Content Creation': [],
  'Email Marketing': [],
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        const data = response.data;
        if (data && data.length > 0) {
          setServices(data);
          setActiveService(data[0].id);
        } else {
          setServices(staticServices);
          setActiveService(staticServices[0].id);
        }
      } catch (error) {
        setServices(staticServices);
        setActiveService(staticServices[0].id);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const currentService = services.find(s => s.id === activeService);
  const currentPortfolio = currentService ? (portfolioByService[currentService.title] || []) : [];

  return (
    <div data-testid="services-page" className="min-h-screen bg-[#050505] pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/5 blur-[120px] rounded-full" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-3xl"
          >
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400">
                Our Services
              </span>
            </motion.div>
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-tight text-[#F8F9FA] font-['Clash_Display'] mb-6"
            >
              Everything You Need to
              <br />
              <span className="text-gradient-blue">Dominate Online</span>
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl leading-relaxed text-slate-300"
            >
              From stunning web design to powerful marketing campaigns, 
              we offer comprehensive digital solutions that drive real results.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Services Section */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Service Tabs - Left Side */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-3">
                <h3 className="text-white font-semibold text-lg mb-4 font-['Clash_Display']">Select a Service</h3>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3,4,5,6].map(i => (
                      <div key={i} className="w-full h-16 bg-[#111] rounded-xl animate-pulse border border-[#2a2a2a]" />
                    ))}
                  </div>
                ) : services.length === 0 ? (
                  <p className="text-slate-400">No services available</p>
                ) : (
                  <div className="space-y-3">
                    {services.map((service) => {
                      const IconComponent = iconMap[service.icon] || Palette;
                      const isActive = activeService === service.id;
                      
                      return (
                        <button
                          key={service.id}
                          onClick={() => setActiveService(service.id)}
                          data-testid={`service-tab-${service.id}`}
                          className={`w-full text-left p-4 rounded-xl transition-all duration-300 group ${
                            isActive
                              ? 'bg-blue-600/20 border-2 border-blue-500/50'
                              : 'bg-[#111] border border-[#2a2a2a] hover:border-blue-500/30 hover:bg-[#151515]'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-11 h-11 rounded-lg flex items-center justify-center transition-all ${
                              isActive ? 'bg-blue-600 shadow-lg shadow-blue-600/30' : 'bg-[#1a1a1a] group-hover:bg-blue-600/20'
                            }`}>
                              <IconComponent size={20} className={isActive ? 'text-white' : 'text-blue-400'} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`font-medium block truncate ${isActive ? 'text-white' : 'text-slate-200'}`}>
                                {service.title}
                              </span>
                              <span className="text-xs text-slate-500">
                                {portfolioByService[service.title]?.length || 0} projects
                              </span>
                            </div>
                            <ArrowRight size={16} className={`transition-all flex-shrink-0 ${isActive ? 'text-blue-400 translate-x-1' : 'text-slate-600'}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Service Details - Right Side */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="lg:col-span-8"
            >
              {currentService && (
                <motion.div
                  key={currentService.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-8"
                >
                  {/* Service Info Card */}
                  <div className="rounded-2xl bg-gradient-to-br from-[#0A0A0A] to-[#0A0A0A] border border-[#1F1F22] p-8 sm:p-10">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-2 block">
                          Service Details
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-white font-['Clash_Display']">
                          {currentService.title}
                        </h2>
                      </div>
                      <div className="w-14 h-14 rounded-xl bg-blue-600/20 border border-blue-500/20 flex items-center justify-center">
                        {iconMap[currentService.icon] && (
                          <span className="text-blue-500">
                            {(() => {
                              const Icon = iconMap[currentService.icon];
                              return <Icon size={28} />;
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-slate-300 leading-relaxed mb-8">
                      {currentService.description}
                    </p>

                    {/* Features */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                      {currentService.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                            <Check size={14} className="text-blue-500" />
                          </div>
                          <span className="text-slate-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to="/quote"
                      data-testid="service-cta-btn"
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Get Started with {currentService.title.split(' ')[0]}
                      <ArrowRight size={18} />
                    </Link>
                  </div>

                  {/* Portfolio for this Service */}
                  {currentPortfolio.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-white font-['Clash_Display'] mb-4">
                        Recent {currentService.title} Projects
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {currentPortfolio.map((project, index) => (
                          <motion.a
                            key={project.title}
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="group relative rounded-xl overflow-hidden aspect-[16/10] block"
                          >
                            <img
                              src={project.image}
                              alt={project.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                              <h4 className="text-white font-semibold">{project.title}</h4>
                              <p className="text-blue-400 text-sm">{project.result}</p>
                              <span className="text-white/60 text-xs mt-1">Click to visit →</span>
                            </div>
                          </motion.a>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#0A0A0A] border-y border-[#1F1F22]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { value: '20+', label: 'Frameworks & Libraries', icon: Users },
              { value: '12+', label: 'Languages', icon: Code2 },
              { value: '35+', label: 'Automation Tools', icon: Award },
              { value: '50+', label: 'Open Source Contributions', icon: TrendingUp },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                variants={itemVariants}
                data-testid={`stat-${index}`}
                className="text-center group"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
                  <stat.icon className="text-blue-500" size={24} />
                </div>
                <p className="text-4xl sm:text-5xl font-bold text-gradient-blue font-['Clash_Display'] mb-2">
                  {stat.value}
                </p>
                <p className="text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={itemVariants}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">
                Why Tegos Digital
              </span>
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#F8F9FA] font-['Clash_Display'] mb-6">
                We Don't Just Build Websites—
                <br />
                <span className="text-gradient-blue">We Build Growth Engines</span>
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                Every business deserves a digital presence that works as hard as they do. 
                We combine creative design with data-driven strategy to create solutions 
                that not only look stunning but deliver measurable results.
              </p>
              <p className="text-slate-400 leading-relaxed">
                From startups to established enterprises, we've helped businesses across 
                industries transform their online presence and accelerate growth.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="grid grid-cols-2 gap-4">
              {[
                { icon: Lightbulb, title: 'Creative Thinking', desc: 'Unique solutions that make you stand out' },
                { icon: Code2, title: 'Clean Code', desc: 'Built to scale and perform' },
                { icon: Palette, title: 'Pixel Perfect', desc: 'Attention to every detail' },
                { icon: TrendingUp, title: 'Growth Focused', desc: 'Strategies that drive ROI' },
              ].map((card, i) => (
                <div key={card.title} className={`p-6 rounded-2xl bg-[#0A0A0A] border border-[#1F1F22] hover:border-blue-500/30 transition-colors ${i >= 2 ? 'mt-0 sm:mt-8' : ''}`}>
                  <card.icon className="text-blue-500 mb-4" size={32} />
                  <h4 className="text-white font-semibold font-['Clash_Display'] mb-2">{card.title}</h4>
                  <p className="text-slate-400 text-sm">{card.desc}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 sm:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <motion.span
              variants={itemVariants}
              className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500"
            >
              Our Values
            </motion.span>
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-medium tracking-tight text-[#F8F9FA] font-['Clash_Display'] mt-4"
            >
              What We Stand For
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              { icon: Zap, title: 'Innovation', description: 'We stay ahead of trends to deliver cutting-edge solutions that set you apart.' },
              { icon: Heart, title: 'Passion', description: 'We love what we do, and it shows in every pixel, every line of code.' },
              { icon: Target, title: 'Results', description: 'We measure success by the growth and results we deliver for our clients.' },
              { icon: Users, title: 'Partnership', description: 'We work alongside you as partners, not just another vendor.' },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                data-testid={`value-${index}`}
                className="group p-8 rounded-2xl bg-[#050505] border border-[#1F1F22] hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="w-14 h-14 mb-6 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center group-hover:bg-blue-600/20 transition-colors">
                  <value.icon className="text-blue-500" size={26} />
                </div>
                <h3 className="text-xl font-semibold text-white font-['Clash_Display'] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#050505] border-t border-[#1F1F22] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/10 blur-[150px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-medium tracking-tight text-[#F8F9FA] font-['Clash_Display'] mb-6"
            >
              Ready to Work With Us?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-lg text-slate-300 max-w-2xl mx-auto mb-10"
            >
              Let's discuss how we can help transform your digital presence 
              and achieve your business goals.
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link
                to="/quote"
                data-testid="services-cta-btn"
                className="btn-primary inline-flex items-center gap-2"
              >
                Schedule a Free Consultation
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
